import axios from 'axios';

// ✅ Utilidad para notificaciones nativas del sistema
const NotificacionNativa = {
  // Verificar soporte y solicitar permisos
  async solicitarPermisos() {
    // console.log('🔐 [NotificacionNativa] Iniciando solicitud de permisos...');
    
    // 1. Verificar si el navegador soporta notificaciones
    if (!("Notification" in window)) {
      console.error('🚫 [NotificacionNativa] Navegador no soporta notificaciones');
      return false;
    }

    // console.log('🔐 [NotificacionNativa] Estado actual:', Notification.permission);

    // 2. Verificar el estado actual de permisos
    if (Notification.permission === "granted") {
      // console.log('✅ [NotificacionNativa] Permisos ya otorgados');
      return true;
    }

    if (Notification.permission === "denied") {
      console.warn('🚫 [NotificacionNativa] Permisos denegados permanentemente');
      // Mostrar instrucciones en la interfaz
      window.dispatchEvent(new CustomEvent('mostrar-instrucciones-notificaciones', { 
        detail: {
          tipo: 'permisos-denegados',
          mensaje: 'Las notificaciones están bloqueadas. Haz clic en el candado 🔒 de la barra de direcciones y permite las notificaciones.'
        }
      }));
      
      return false;
    }

    // 3. Solicitar permisos (solo funciona con interacción del usuario)
    try {
      // console.log('🔐 [NotificacionNativa] Solicitando permisos al usuario...');
      const permission = await Notification.requestPermission();
      // console.log('🔐 [NotificacionNativa] Respuesta del usuario:', permission);
      
      if (permission === "granted") {
        // console.log('✅ [NotificacionNativa] Permisos otorgados exitosamente');
        return true;
      } else {
        console.warn('🚫 [NotificacionNativa] Usuario rechazó los permisos');
        // Mostrar instrucciones para reactivar
        window.dispatchEvent(new CustomEvent('mostrar-instrucciones-notificaciones', { 
          detail: {
            tipo: 'permisos-rechazados',
            mensaje: 'Para recibir notificaciones: haz clic en el candado 🔒 de la barra de direcciones → Notificaciones → Permitir → Recargar página'
          }
        }));
        
        return false;
      }
    } catch (error) {
      console.error('💥 [NotificacionNativa] Error al solicitar permisos:', error);
      return false;
    }
  },

  // Mostrar notificación nativa
  mostrar(titulo, opciones = {}) {
    if (Notification.permission !== "granted") {
      console.warn('🚫 [NotificacionNativa] Permisos no otorgados. Estado:', Notification.permission);
      return null;
    }

    const defaultOptions = {
      body: opciones.body || 'Nueva alerta en el sistema EnerHVAC',
      icon: opciones.icon || `${location.origin}/images/logo.png`,
      badge: opciones.badge || `${location.origin}/images/logo.png`,
      tag: opciones.tag || 'enerhvac-alert',
      requireInteraction: opciones.requireInteraction || false,
      silent: opciones.silent || false,
      timestamp: Date.now(),
      dir: 'auto',
      lang: 'es-ES',
      ...opciones
    };

    // console.log('📢 [NotificacionNativa] Intentando mostrar notificación:', { titulo, opciones: defaultOptions });

    try {
      const notification = new Notification(titulo, defaultOptions);

      // console.log('✅ [NotificacionNativa] Notificación creada exitosamente');

      // Eventos de la notificación
      notification.onshow = () => {
        // console.log('👁️ [NotificacionNativa] Notificación mostrada en pantalla');
      };

      notification.onclick = () => {
        // console.log('🖱️ [NotificacionNativa] Usuario hizo clic en la notificación');
        window.focus(); // Enfoca la ventana de la aplicación
        notification.close();
        if (opciones.onClick) opciones.onClick();
      };

      notification.onerror = (error) => {
        console.error('❌ [NotificacionNativa] Error al mostrar notificación:', error);
      };

      notification.onclose = () => {
        // console.log('🔕 [NotificacionNativa] Notificación cerrada');
      };

      // Auto-cerrar después de tiempo especificado o 8 segundos por defecto
      const autoCloseTime = opciones.autoClose !== false ? (opciones.autoCloseTime || 8000) : null;
      if (autoCloseTime) {
        setTimeout(() => {
          if (notification) {
            // console.log('⏰ [NotificacionNativa] Auto-cerrando notificación después de', autoCloseTime, 'ms');
            notification.close();
          }
        }, autoCloseTime);
      }

      return notification;
    } catch (error) {
      console.error('💥 [NotificacionNativa] Excepción al crear notificación:', error);
      return null;
    }
  },

  // ✅ Método para probar notificaciones
  probar() {
    // console.log('🧪 [NotificacionNativa] Iniciando prueba de notificación...');
    // console.log('🧪 [NotificacionNativa] Estado de permisos:', Notification.permission);
    
    // Verificar permisos antes de mostrar
    if (Notification.permission !== "granted") {
      // Mostrar mensaje personalizado según el estado
      let mensaje = '';
      if (Notification.permission === "denied") {
        mensaje = 'Notificaciones bloqueadas. Haz clic en el candado 🔒 de la barra de direcciones para permitirlas.';
      } else {
        mensaje = 'Primero debes permitir las notificaciones cuando aparezca la solicitud.';
      }
      
      console.warn('🚫 [NotificacionNativa] No se puede probar:', mensaje);
      
      window.dispatchEvent(new CustomEvent('mostrar-instrucciones-notificaciones', { 
        detail: {
          tipo: 'no-se-puede-probar',
          mensaje: mensaje
        }
      }));
      
      return null;
    }
    
    // console.log('✅ [NotificacionNativa] Permisos OK, mostrando notificación de prueba...');
    
    return this.mostrar('🔔 EnerHVAC - Prueba', {
      body: '✅ ¡Las notificaciones están funcionando correctamente!\n\nAhora recibirás alertas del sistema.',
      tag: 'test-notification',
      icon: `${location.origin}/images/logo.png`,
      requireInteraction: true, // Forzar que se mantenga visible
      onClick: () => {
        // console.log('✅ [NotificacionNativa] Prueba completada - usuario hizo clic');
      }
    });
  }
};

// ✅ Singleton global para evitar múltiples instancias
let controladorGlobal = null;

export function iniciarPopupAlertas(store) {
  // ✅ Si ya existe un controlador, devolver el existente
  if (controladorGlobal && controladorGlobal.store === store) {
    // console.log('🔄 [Popup] Reutilizando controlador existente');
    return controladorGlobal.instance;
  }

  // ✅ Detener controlador anterior si existe
  if (controladorGlobal) {
    // console.log('🛑 [Popup] Deteniendo controlador anterior');
    controladorGlobal.instance.stop();
    controladorGlobal = null;
  }

  const INTERVALO = 2 * 60 * 1000; // 2 minutos
  let intervaloId = null;
  let ejecutando = false;
  let permisosNotificacion = false;
  
  // ✅ Sistema de deduplicación para evitar eventos duplicados
  let ultimaVerificacion = 0;
  let ultimaNotificacion = 0; // ✅ Tiempo de la última notificación mostrada
  let ultimasAlertas = [];
  const MINIMO_INTERVALO_VERIFICACION = 5000; // 5 segundos mínimo entre verificaciones
  
  // ✅ Sistema de cooldown para notificaciones (24 horas)
  let alertasNotificacionCooldown = new Map();
  
  // ✅ Cargar cooldown desde localStorage
  const cargarNotificacionCooldown = () => {
    try {
      const cooldownData = localStorage.getItem('notificacionesCooldown');
      if (cooldownData) {
        const data = JSON.parse(cooldownData);
        alertasNotificacionCooldown = new Map(Object.entries(data));
        limpiarNotificacionCooldownExpirado();
      }
    } catch (error) {
      console.error('Error al cargar cooldown de notificaciones:', error);
      alertasNotificacionCooldown = new Map();
    }
  };
  
  // ✅ Guardar cooldown en localStorage
  const guardarNotificacionCooldown = () => {
    try {
      const cooldownObj = Object.fromEntries(alertasNotificacionCooldown);
      localStorage.setItem('notificacionesCooldown', JSON.stringify(cooldownObj));
    } catch (error) {
      console.error('Error al guardar cooldown de notificaciones:', error);
    }
  };
  
  // ✅ Verificar si una alerta está en cooldown para notificaciones
  const estaEnNotificacionCooldown = (alertaId) => {
    if (!alertaId || !alertasNotificacionCooldown.has(alertaId.toString())) return false;
    
    const timestampLeida = alertasNotificacionCooldown.get(alertaId.toString());
    const ahora = Date.now();
    const unDiaEnMs = 24 * 60 * 60 * 1000; // 24 horas en milisegundos
    
    return (ahora - timestampLeida) < unDiaEnMs;
  };
  
  // ✅ Limpiar cooldown expirado de notificaciones
  const limpiarNotificacionCooldownExpirado = () => {
    const ahora = Date.now();
    const unDiaEnMs = 24 * 60 * 60 * 1000;
    
    for (const [alertaId, timestamp] of alertasNotificacionCooldown.entries()) {
      if ((ahora - timestamp) >= unDiaEnMs) {
        alertasNotificacionCooldown.delete(alertaId);
      }
    }
  };
  
  // ✅ Marcar alerta como notificada (agregar al cooldown)
  const marcarAlertaNotificada = (alertaId) => {
    if (alertaId) {
      alertasNotificacionCooldown.set(alertaId.toString(), Date.now());
      guardarNotificacionCooldown();
      // console.log(`🔕 [Popup] Alerta ${alertaId} marcada como notificada, cooldown de 24h para notificaciones nativas`);
    }
  };

  // ✅ Escuchar cuando una alerta se marca como leída para agregar al cooldown
  const manejarAlertaLeida = (event) => {
    const alertaId = event.detail?.alertaId || event.detail?.id;
    if (alertaId) {
      // console.log(`📖 [Popup] Alerta ${alertaId} marcada como leída, agregando a cooldown de notificaciones`);
      marcarAlertaNotificada(alertaId);
    }
  };

  const titleCase = (str) => {
    if (!str && str !== 0) return '';
    return String(str)
      .toLowerCase()
      .split(' ')
      .map(s => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ');
  };

  const construirUbicacion = (alerta) => {
    const partes = [];
    const map = {
      municipio: ['municipio', 'mcpio'],
      sede: ['sede', 'sede_nombre'],
      bloque: ['bloque_edificio', 'bloque', 'bloque_nombre'],
      area: ['area_especifica', 'area', 'area_nombre'],
      cbi: ['cbi']
    };
    for (const key of map.municipio) if (alerta[key]) partes.push(titleCase(alerta[key]));
    for (const key of map.sede) if (alerta[key]) partes.push(titleCase(alerta[key]));
    for (const key of map.bloque) if (alerta[key]) partes.push(titleCase(alerta[key]));
    for (const key of map.area) if (alerta[key]) partes.push(titleCase(alerta[key]));
    if (alerta.cbi) partes.push(String(alerta.cbi));
    if (partes.length === 0) {
      ['municipio','sede','bloque_edificio','area_especifica','cbi'].forEach(k => {
        if (alerta[k]) partes.push(titleCase(alerta[k]));
      });
    }
    return partes.join(' / ');
  };

  async function verificarPopup() {
    const ahora = Date.now();
    
    // ✅ Prevenir verificaciones muy frecuentes
    if (ahora - ultimaVerificacion < MINIMO_INTERVALO_VERIFICACION) {
      return;
    }
    
    // ✅ 2. Evitar ejecuciones múltiples y verificar autenticación
    if (ejecutando) return;
    
    // ✅ 3. No mostrar alertas si no estamos autenticados
    if (!store.state.user || !store.state.user.token || !store.state.auth) {
      return;
    }
    
    // ✅ 4. No mostrar alertas en páginas de login
    const rutaActual = window.location.hash || window.location.pathname;
    const rutasExcluidas = ['sign-in', 'login', 'ingresar', 'recuperar-clave', 'reset-password', 'sign-up'];
    const esPaginaLogin = rutasExcluidas.some(ruta => rutaActual.toLowerCase().includes(ruta));
    
    if (esPaginaLogin) {
      // console.log('🚫 [Popup] Bloqueado en página de login:', rutaActual);
      return;
    }
    
    ejecutando = true;
    ultimaVerificacion = ahora;
    try {
      const resp = await axios.get('/api/popup-alerta-actividades', {
        headers: { Authorization: `Bearer ${store.state.user.token}` }
      });
      
      const datos = resp.data;
      const alertas = Array.isArray(datos) ? datos : (Array.isArray(datos.alertas) ? datos.alertas : []);
      if (alertas && alertas.length > 0) {
        // Normaliza la info
        const payload = alertas.map(a => {
          const idDetalle = Number(a.detallealerta_id ?? a.detalle_alerta_id ?? 0);
          return {
            id: a.id ?? a.alerta_id ?? a._id,
            detallealerta_id: idDetalle,
            titulo: titleCase(a.detalle_alerta ?? a.descripcion ?? ''),
            ubicacion: construirUbicacion(a),
            raw: a
          };
        });

        // ✅ Verificar si son las mismas alertas que la verificación anterior
        const alertasString = JSON.stringify(payload.map(p => ({ id: p.id, detallealerta_id: p.detallealerta_id })));
        const sonMismasAlertas = alertasString === JSON.stringify(ultimasAlertas);
        
        if (sonMismasAlertas && ultimaNotificacion > 0) {
          // ✅ Mostrar recordatorio cada 6 minutos para alertas persistentes
          const tiempoSinNotificacion = Date.now() - ultimaNotificacion;
          const RECORDATORIO_INTERVALO = 6 * 60 * 1000; // 6 minutos
          
          if (tiempoSinNotificacion < RECORDATORIO_INTERVALO) {
            // console.log('🔕 [Popup] Mismas alertas y recordatorio muy reciente, saltando... (próximo en', Math.round((RECORDATORIO_INTERVALO - tiempoSinNotificacion) / 60000), 'minutos)');
            return;
          } else {
            // console.log('🔔 [Popup] Mostrando recordatorio de alertas persistentes');
          }
        }
        
        // Actualizar últimas alertas y tiempo de notificación
        ultimasAlertas = payload.map(p => ({ id: p.id, detallealerta_id: p.detallealerta_id }));
        ultimaNotificacion = Date.now();
        
        // console.log('📢 [Popup] Disparando notificaciones:', sonMismasAlertas ? 'RECORDATORIO' : 'NUEVAS ALERTAS');

        // ✅ Filtrar alertas que están en cooldown para notificaciones nativas
        const alertasParaNotificacion = payload.filter(alerta => {
          if (!alerta.id) return true; // Si no tiene ID, mostrar por seguridad
          
          const enCooldown = estaEnNotificacionCooldown(alerta.id);
          if (enCooldown) {
            // console.log(`🔕 [Popup] Alerta ${alerta.id} en cooldown para notificaciones nativas (24h)`);
          }
          return !enCooldown;
        });

        // console.log(`🔍 [Popup] Filtrado notificaciones: ${payload.length} total → ${alertasParaNotificacion.length} para mostrar`);
        // console.log(`🔍 [Popup] Permisos notificación: ${permisosNotificacion}`);

        // ✅ Mostrar notificaciones nativas del sistema si están habilitadas y hay alertas no filtradas
        if (permisosNotificacion && alertasParaNotificacion.length > 0) {
          // ✅ 1. Mostrar múltiples alertas con colores identificativos
          if (alertasParaNotificacion.length === 1) {
            // Una sola alerta
            const alerta = alertasParaNotificacion[0];
            const prefijoTitulo = sonMismasAlertas ? '🔔 RECORDATORIO' : '🚨 NUEVA ALERTA';
            const tituloNotificacion = `${prefijoTitulo} - EnerHVAC`;
            const cuerpoNotificacion = `${alerta.titulo}\n📍 ${alerta.ubicacion}`;

            // console.log('📢 [Popup] Mostrando notificación única:', { titulo: tituloNotificacion, alerta: alerta.id });
            
            const notification = NotificacionNativa.mostrar(tituloNotificacion, {
              body: cuerpoNotificacion,
              tag: sonMismasAlertas ? 'enerhvac-recordatorio-single' : 'enerhvac-alerta-single',
              icon: `${location.origin}/images/logo.png`,
              requireInteraction: true, // Forzar que sea visible y requiera interacción
              silent: false, // Asegurar que no esté silenciosa
              onClick: () => {
                // ✅ NO marcar como notificada aquí - solo cuando se marque como leída
                // console.log('🖱️ [Popup] Usuario hizo clic en notificación única (no marcando cooldown)');
              }
            });

            // ✅ NO marcar automáticamente - solo mostrar la notificación
          } else {
            // Múltiples alertas - crear una notificación agrupada
            const colores = ['🔴', '🟠', '🟡', '🔵', '🟢', '🟣', '🟤']; // Colores para identificar
            const textoAlertas = sonMismasAlertas ? 'alertas activas' : 'nuevas alertas';
            let cuerpoMultiple = `⚠️ ${alertasParaNotificacion.length} ${textoAlertas}:\n\n`;
            
            alertasParaNotificacion.slice(0, 5).forEach((alerta, index) => { // Mostrar máximo 5 alertas
              const color = colores[index % colores.length];
              cuerpoMultiple += `${color} ${alerta.titulo}\n📍 ${alerta.ubicacion}\n\n`;
            });

            if (alertasParaNotificacion.length > 5) {
              cuerpoMultiple += `➕ Y ${alertasParaNotificacion.length - 5} alerta(s) más...`;
            }

            const tituloMultiple = sonMismasAlertas ? '🔔 EnerHVAC - Recordatorio Alertas' : '🚨 EnerHVAC - Múltiples Alertas';
            // console.log('📢 [Popup] Mostrando notificación múltiple:', { titulo: tituloMultiple, cantidad: alertasParaNotificacion.length });
            
            const notification = NotificacionNativa.mostrar(tituloMultiple, {
              body: cuerpoMultiple.trim(),
              tag: sonMismasAlertas ? 'enerhvac-recordatorio-multiple' : 'enerhvac-alertas-multiple',
              icon: `${location.origin}/images/logo.png`,
              requireInteraction: true, // Mantener visible hasta que el usuario interactúe
              silent: false, // Asegurar que no esté silenciosa
              onClick: () => {
                // ✅ NO marcar como notificadas aquí - solo cuando se marquen como leídas
                // console.log('🖱️ [Popup] Usuario hizo clic en notificación múltiple (no marcando cooldown)');
              }
            });

            // ✅ NO marcar automáticamente - solo mostrar las notificaciones
          }
        } else if (permisosNotificacion && payload.length > 0 && alertasParaNotificacion.length === 0) {
          // console.log('🔕 [Popup] Todas las alertas están en cooldown para notificaciones nativas');
        } else if (!permisosNotificacion && payload.length > 0) {
          console.warn('🚫 [Popup] No se pueden mostrar notificaciones - permisos no otorgados');
          // console.log('🚆 [Popup] Ejecutar window.verificarPermisosNotificacion() para solicitar permisos');
        }

        // ✅ Disparar evento solo si hay nuevas alertas
        window.dispatchEvent(new CustomEvent('toast-alertas', { detail: payload }));
      }
    } catch (err) {
      // silencioso
    } finally {
      ejecutando = false;
    }
  }

  async function start() {
    // ✅ 3. Solo iniciar si estamos autenticados
    if (!store.state.user || !store.state.user.token || !store.state.auth) {
      return;
    }
    
    // ✅ 4. No iniciar en páginas de login
    const rutaActual = window.location.hash || window.location.pathname;
    const rutasExcluidas = ['sign-in', 'login', 'ingresar', 'recuperar-clave', 'reset-password', 'sign-up'];
    const esPaginaLogin = rutasExcluidas.some(ruta => rutaActual.toLowerCase().includes(ruta));
    
    if (esPaginaLogin) {
      // console.log('🚫 [Popup] No iniciando en página de login:', rutaActual);
      return;
    }

    // ✅ Cargar cooldown de notificaciones desde localStorage
    cargarNotificacionCooldown();

    // ✅ Escuchar cuando las alertas se marcan como leídas
    window.addEventListener('alerta-marcada-leida', manejarAlertaLeida);

    // ✅ Solicitar permisos para notificaciones nativas al iniciar
    // console.log('🔐 [Popup] Solicitando permisos de notificación...');
    permisosNotificacion = await NotificacionNativa.solicitarPermisos();
    // console.log('🔐 [Popup] Estado de permisos de notificación:', permisosNotificacion);
    
    // ✅ 2. Evitar intervalos duplicados
    if (intervaloId) {
      clearInterval(intervaloId);
      intervaloId = null;
    }
    
    // Primera verificación inmediata
    verificarPopup();
    
    // Configurar intervalo periódico cada 2 minutos
    // console.log('⏰ [Popup] Sistema iniciado - verificaciones cada', INTERVALO / 1000, 'segundos');
    intervaloId = setInterval(() => {
      // console.log('🔔 [Popup] Ejecutando verificación programada:', new Date().toLocaleTimeString());
      verificarPopup();
    }, INTERVALO);
    
    // ✅ Limpiar cooldown expirado cada hora
    setInterval(() => {
      limpiarNotificacionCooldownExpirado();
      guardarNotificacionCooldown();
    }, 60 * 60 * 1000); // Cada hora
  }

  function stop() {
    if (intervaloId) {
      clearInterval(intervaloId);
      intervaloId = null;
    }
    
    // ✅ Remover listener de alertas leídas
    window.removeEventListener('alerta-marcada-leida', manejarAlertaLeida);
    
    // ✅ Limpiar singleton cuando se detiene
    if (controladorGlobal) {
      controladorGlobal = null;
    }
  }

  const instance = { 
    start, 
    stop, 
    verificarAhora: verificarPopup,
    // ✅ Exponer utilidades de notificaciones nativas
    notificacionNativa: NotificacionNativa,
    habilitarNotificaciones: async () => {
      permisosNotificacion = await NotificacionNativa.solicitarPermisos();
      return permisosNotificacion;
    },
    get tienePermisosNotificacion() {
      return permisosNotificacion;
    },
    // ✅ Método para probar notificaciones desde consola
    probarNotificacion: () => {
      // console.log('🧪 [Popup] Prueba manual de notificación solicitada');
      // console.log('🧪 [Popup] Permisos actuales:', permisosNotificacion);
      // console.log('🧪 [Popup] Estado Notification.permission:', Notification.permission);
      
      if (permisosNotificacion) {
        return NotificacionNativa.probar();
      } else {
        console.warn('🚫 [Popup] No se puede probar - permisos no otorgados');
        // Intentar solicitar permisos nuevamente
        NotificacionNativa.solicitarPermisos().then(granted => {
          if (granted) {
            // console.log('✅ [Popup] Permisos otorgados, probando notificación...');
            NotificacionNativa.probar();
          }
        });
        return null;
      }
    },
    // ✅ Método para verificar si debe ejecutarse
    get debeEjecutarse() {
      return !!(store.state.user && store.state.user.token && store.state.auth);
    },
    // ✅ Estado actual
    get estado() {
      const ahora = Date.now();
      const tiempoTranscurrido = ultimaVerificacion ? (ahora - ultimaVerificacion) / 1000 : 0;
      
      return {
        ejecutando,
        permisosNotificacion,
        autenticado: instance.debeEjecutarse,
        intervaloActivo: !!intervaloId,
        intervaloId: intervaloId,
        ultimaVerificacion: ultimaVerificacion ? new Date(ultimaVerificacion).toLocaleTimeString() : 'Nunca',
        segundosDesdeUltimaVerificacion: Math.round(tiempoTranscurrido),
        proximaVerificacion: intervaloId ? new Date(ultimaVerificacion + INTERVALO).toLocaleTimeString() : 'N/A',
        deberiaHaberEjecutado: tiempoTranscurrido > (INTERVALO / 1000)
      };
    },
    
    // ✅ Método para forzar verificación manual
    forzarVerificacion() {
      // console.log('🔧 [Popup] Forzando verificación manual...');
      verificarPopup();
    },
    
    // ✅ Método para reiniciar el sistema
    reiniciar() {
      // console.log('🔄 [Popup] Reiniciando sistema completo...');
      instance.stop();
      setTimeout(() => {
        instance.start();
      }, 1000);
    }
  };

  // ✅ Registrar en singleton global
  controladorGlobal = { store, instance };

  return instance;
}

// ✅ Función para obtener el controlador global existente
export function obtenerControladorPopup() {
  return controladorGlobal ? controladorGlobal.instance : null;
}

// ✅ Función para verificar si existe un controlador activo
export function tieneControladorActivo() {
  return !!(controladorGlobal && controladorGlobal.instance);
}

// ✅ Función de debugging para consola
export function debugPopup() {
  if (!controladorGlobal) {
    // console.log('❌ [Debug] No hay controlador popup activo');
    return null;
  }
  
  const estado = controladorGlobal.instance.estado;
  // console.log('🔍 [Debug] Estado del controlador popup:', estado);
  // console.log('📊 [Debug] Store state:', {
  //   auth: controladorGlobal.store.state.auth,
  //   user: !!controladorGlobal.store.state.user,
  //   token: !!controladorGlobal.store.state.user?.token
  // });
  
  if (estado.deberiaHaberEjecutado) {
    // console.log('⚠️ [Debug] ¡ATENCIÓN! El intervalo debería haber ejecutado pero no lo hizo');
    // console.log('🔧 [Debug] Puedes usar: window.forzarVerificacionPopup() o window.reiniciarPopup()');
  }
  
  return estado;
}

// ✅ Funciones auxiliares para debugging
export function forzarVerificacionPopup() {
  if (controladorGlobal) {
    // console.log('🔧 [Debug] Forzando verificación inmediata...');
    controladorGlobal.instance.forzarVerificacion();
  } else {
    // console.log('❌ No hay controlador activo');
  }
}

export function reiniciarPopup() {
  if (controladorGlobal) {
    controladorGlobal.instance.reiniciar();
  } else {
    // console.log('❌ No hay controlador activo');
  }
}

// ✅ Hacer funciones de debug disponibles globalmente
if (typeof window !== 'undefined') {
  window.debugPopup = debugPopup;
  window.forzarVerificacionPopup = forzarVerificacionPopup;
  window.reiniciarPopup = reiniciarPopup;
  
  // ✅ Funciones adicionales para debug de notificaciones
  window.probarNotificacion = () => {
    if (controladorGlobal) {
      return controladorGlobal.instance.probarNotificacion();
    } else {
      console.log('❌ No hay controlador activo');
      // Probar notificación directamente
      return NotificacionNativa.probar();
    }
  };
  
  window.verificarPermisosNotificacion = async () => {
    console.log('🔐 [Debug] Verificando permisos de notificación...');
    console.log('🔐 [Debug] Notification.permission:', Notification.permission);
    console.log('🔐 [Debug] Soporte de notificaciones:', 'Notification' in window);
    
    if (Notification.permission === 'default') {
      console.log('🔐 [Debug] Solicitando permisos...');
      const resultado = await NotificacionNativa.solicitarPermisos();
      console.log('🔐 [Debug] Resultado solicitud:', resultado);
      return resultado;
    }
    
    return Notification.permission === 'granted';
  };
  
  window.notificacionDirecta = (titulo = '🧪 Prueba Directa', body = 'Esta es una notificación de prueba directa') => {
    console.log('🧪 [Debug] Creando notificación directa...');
    console.log('🧪 [Debug] Notification.permission:', Notification.permission);
    
    if (Notification.permission !== 'granted') {
      console.error('🚫 [Debug] Permisos no otorgados. Ejecutar window.verificarPermisosNotificacion()');
      return null;
    }
    
    try {
      const notification = new Notification(titulo, {
        body: body,
        icon: `${location.origin}/images/logo.png`,
        requireInteraction: true,
        tag: 'debug-test',
        silent: false
      });
      
      notification.onshow = () => console.log('👁️ [Debug] Notificación directa mostrada');
      notification.onclick = () => {
        console.log('🖱️ [Debug] Click en notificación directa');
        notification.close();
      };
      notification.onerror = (e) => console.error('❌ [Debug] Error notificación directa:', e);
      notification.onclose = () => console.log('🔕 [Debug] Notificación directa cerrada');
      
      return notification;
    } catch (error) {
      console.error('💥 [Debug] Excepción notificación directa:', error);
      return null;
    }
  };

  // ✅ Función para forzar notificación con datos reales (bypass cooldown)
  window.forzarNotificacionAlerta = () => {
    console.log('🚨 [Debug] Forzando notificación con bypass de cooldown...');
    
    if (controladorGlobal) {
      // Crear una notificación de prueba con formato real
      NotificacionNativa.mostrar('🚨 EnerHVAC - Alerta Forzada', {
        body: 'A006: El Aire Esta Próximo A Requerir Mantenimiento Preventivo\n📍 Montería / Sede Campus Universitario / Bloque 7',
        tag: 'debug-forced-alert',
        icon: `${location.origin}/images/logo.png`,
        requireInteraction: true,
        silent: false,
        onClick: () => {
          console.log('🖱️ [Debug] Click en alerta forzada');
        }
      });
    } else {
      console.error('❌ [Debug] No hay controlador activo');
    }
  };

  // ✅ Función para limpiar cooldown y probar
  window.limpiarCooldownYProbar = () => {
    console.log('🧹 [Debug] Limpiando cooldown de notificaciones...');
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('notificacionesCooldown');
      console.log('✅ [Debug] Cooldown limpiado');
    }
    
    // Forzar verificación inmediata
    setTimeout(() => {
      if (controladorGlobal) {
        controladorGlobal.instance.forzarVerificacion();
      }
    }, 1000);
  };
  
  /* console.log('🛠️ [Popup] Funciones de debug disponibles:');
  console.log('   - window.probarNotificacion()');
  console.log('   - window.verificarPermisosNotificacion()');
  console.log('   - window.notificacionDirecta()');
  console.log('   - window.forzarNotificacionAlerta()');
  console.log('   - window.limpiarCooldownYProbar()');
  console.log('   - window.debugPopup()'); */
}